import { Breakpoints, Button, Icon } from "swing-components";

import { Question } from "~onboarding/components";
import type { StepComputed, UnsyncedSubAttribute } from "~onboarding/utils";
import { translate } from "~utils";

type StepProps = {
  stepComputed: Omit<StepComputed, "prevButtonText" | "nextButtonText">;
  /**
   * When answering a Question, this function will pass the `value` to the
   * parent TaskPage.
   */
  onChange: ({ attribute, value }: UnsyncedSubAttribute) => void;
  /**
   * Disabled the ability to answer any questions.
   * @default false
   */
  isReadOnly?: boolean;
  /**
   * Helps calculate the correct "Next" button label
   * @default false
   */
  isLastStep?: boolean;
  /**
   * Button props to for next button.
   */
  nextButtonProps: {
    label?: string;
    onClick: () => void;
    /**
     * Shows a loading spinner on the button
     * @default false
     */
    isLoading?: boolean;
    /**
     * @default false
     */
    disabled?: boolean;
  };
  /**
   * Button props to for prev button.
   */
  prevButtonProps?: {
    label?: string;
    onClick: () => void;
    /**
     * @default false
     */
    disabled?: boolean;
    // NOTE: This button cannot be is a loading state.
  };
};

export function Step(props: StepProps) {
  const {
    stepComputed,
    prevButtonProps,
    nextButtonProps,
    onChange,
    isReadOnly = false,
    isLastStep = false,
  } = props;
  const { status, questions, header } = stepComputed;

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        gap: "var(--spacing-6xl)",
      }}
    >
      {/* Header */}
      {header && (
        <header css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h3 css={{ margin: 0, color: "black" }}> {header.title} </h3>
          {header.decorations && (
            <ul
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-lg)",
                paddingLeft: "var(--spacing-lg)",
                margin: 0,
              }}
            >
              {header.decorations.map(({ icon, label }) => (
                <li
                  key={`${header.title}-${label}`}
                  css={{ display: "flex", gap: "var(--spacing-lg)" }}
                >
                  <Icon name={icon} />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          )}
          {/* Divider */}
          <hr css={{ width: "100%", borderWidth: 0 }} />
        </header>
      )}

      {/* Questions */}
      {questions.map((question) => (
        <Question
          key={question.attribute}
          {...question}
          value={question.value}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />
      ))}

      {/* Buttons */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-lg)",
          justifyContent: "space-between",
          [Breakpoints.mediumDeviceAndUp]: {
            flexDirection: "row-reverse",
          },
        }}
      >
        <div>
          <Button
            type="primary"
            onClick={nextButtonProps.onClick}
            status={
              nextButtonProps.disabled
                ? "disabled"
                : nextButtonProps.isLoading
                  ? "loading"
                  : "enabled"
            }
            isFluid
          >
            {getNextButtonLabel({
              label: nextButtonProps?.label,
              stepStatus: status,
              isLastStep,
              isReadOnly,
            })}
          </Button>
        </div>
        {prevButtonProps && (
          <div>
            <Button
              type="secondary"
              onClick={prevButtonProps.onClick}
              status={prevButtonProps.disabled ? "disabled" : "enabled"}
              isFluid
            >
              {prevButtonProps.label ?? translate("LABEL_BACK")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/********** Helper Functions *********/
/**
 * Returns the appropriate next button label following these rules:
 *
 * 1. In any case that the current Step is in a UNDER_REVIEW status, show a return home button since
 *   the user cannot move forward.
 * 2. If on the last step and NOT in a read-only state, return either the given label or "Submit"
 *   since this can be customized in the Step configuration.
 * 3. When on the last step and IN a read-only state, return "Return Home" since the user cannot move
 * forward nor control this label.
 * 4. Otherwise, default to the given label or "Next"
 * */
function getNextButtonLabel(params: {
  label?: string;
  stepStatus: StepComputed["status"];
  isLastStep: boolean;
  isReadOnly: boolean;
}) {
  const { label, stepStatus, isLastStep, isReadOnly } = params;
  // If the Step is in a UNDER_REVIEW status, show a return home button.
  if (stepStatus === "UNDER_REVIEW") return "Return Home";
  // When ON the last step and NOT IN a read-only state
  // Then return either the label or "Submit"
  if (isLastStep && !isReadOnly) return label ?? "Submit";
  // When ON the last step and IN a read-only state
  if (isLastStep && isReadOnly) return "Return Home";
  // Otherwise, default to the given label or "Next"
  return label ?? "Next";
}
